// src/app/core/voice.service.ts
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class VoiceService {

  // One AudioContext for the whole service
  private ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

  /**
   * Play a short sine‑wave beep.
   * @param duration  milliseconds (default 120 ms)
   * @param freq      hertz (default 880 Hz ≈ “A₅”)
   * @param volume    0‑1 (default 0.25)
   * @returns Promise that settles after the beep ends.
   */
  beep(duration = 120, freq = 880, volume = 0.25): Promise<void> {
    return new Promise<void>(async resolve => {
      // iOS/Safari may start suspended until a user gesture
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      const osc   = this.ctx.createOscillator();
      const gain  = this.ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = volume;

      osc.connect(gain).connect(this.ctx.destination);

      const now = this.ctx.currentTime;
      osc.start(now);
      osc.stop(now + duration / 1000);

      osc.onended = () => resolve();
    });
  }

  /**
   * Speak text via the Web Speech API.
   */
  speak(text: string, lang = 'en-US', voiceName?: string): void {
    if (!('speechSynthesis' in window)) {
      console.warn('Speech‑synthesis not supported.');
      return;
    }
    window.speechSynthesis.cancel();

    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;

    if (voiceName) {
      const chosen = window.speechSynthesis.getVoices().find(v => v.name === voiceName);
      if (chosen) u.voice = chosen;
    }

    window.speechSynthesis.speak(u);
  }

  /**
   * Convenience: beep, then speak.
   */
  async playSuccessVoice(text: string): Promise<void> {
    await this.beep();           // default 880 Hz, 120 ms
    this.speak(text);
  }
}
